import type { SearchEndpointResponse } from '@ps/isp-web-services-types';

import type { WidgetEffectParams } from '@swf/core/store';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import getErrorCode from '@swf/sports/utils/getErrorCode';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';
import { hasRequestAborted } from '@swf/sports/utils/hasRequestAborted';

import type { AzMenuSuccessResponse, PopularSuccessResponse } from '../types';

import type { WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, widget, select, rootState } = params;

    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const widgetSelect: WidgetSelect = select[widget.uid];

    const { menuAzRequested, menuPopularRequested, clear }: WidgetDispatch = widgetDispatch;

    if (!widgetSelect.hasPreloaded(rootState)) {
      await Promise.all([menuAzRequested(), menuPopularRequested()]);
    }

    widget.unmount.subscribe(clear);
  },
  async menuAzRequested(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, request, widget, rootState } = params;
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const sportsRequest = buildSportsRequest(request, rootState);

    try {
      const {
        body: { data }
      } = await sportsRequest<AzMenuSuccessResponse>({
        path: '/a-z',
        bypassBffCache: false,
        signal: getWidgetCancellationSignal(params)
      });

      widgetDispatch.dataAzLoadSuccess(data);
    } catch (error) {
      if (hasRequestAborted(error)) return;
      widgetDispatch.dataAzLoadFailed(getErrorCode(error));
    }
  },
  async menuPopularRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ) {
    const { dispatch, request, widget, rootState } = params;
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const sportsRequest = buildSportsRequest(request, rootState);

    try {
      const {
        body: { data }
      } = await sportsRequest<PopularSuccessResponse>({
        path: '/links/popular',
        bypassBffCache: false,
        signal: getWidgetCancellationSignal(params)
      });
      widgetDispatch.dataPopularSuccess(data);
    } catch {
      // silent error
    }
  },
  async searchRequested(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, request, widget, rootState } = params;
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const sportsRequest = buildSportsRequest(request, rootState);
    widgetDispatch.searchLoadRequest();
    try {
      const { body } = await sportsRequest<SearchEndpointResponse>({
        path: '/search'
      });
      widgetDispatch.searchLoadedSuccessfully(body);
    } catch {
      widgetDispatch.searchLoadedUnsuccessfully();
    }
  }
};
